import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Trash2, Pencil, Plus, X } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatGBP } from "@/lib/format";

const emptyProduct = {
  name: "",
  brand: "æxènts",
  description: "",
  notes_top: "",
  notes_heart: "",
  notes_base: "",
  price: 0,
  size_ml: 100,
  category: "unisex",
  concentration: "Eau de Parfum",
  image_url: "",
  gallery: "",
  stock: 100,
  featured: false,
};

export default function AdminPage() {
  const { user, checked } = useAuth();
  const [tab, setTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [editing, setEditing] = useState(null); // product being edited or `new`
  const [form, setForm] = useState(emptyProduct);
  const [error, setError] = useState("");

  const loadProducts = async () => {
    const { data } = await api.get("/products");
    setProducts(data);
  };
  const loadOrders = async () => {
    const { data } = await api.get("/admin/orders");
    setOrders(data);
  };
  const loadSubscribers = async () => {
    const { data } = await api.get("/admin/subscribers");
    setSubscribers(data);
  };

  useEffect(() => {
    if (user && user.role === "admin") {
      loadProducts();
      loadOrders();
      loadSubscribers();
    }
  }, [user]);

  if (!checked) return <div className="py-24 text-center text-muted-foreground">Loading…</div>;
  if (user === false) return <Navigate to="/login" state={{ from: "/admin" }} replace />;
  if (user && user.role !== "admin")
    return (
      <div className="py-24 text-center text-muted-foreground" data-testid="admin-forbidden">
        Access denied — admin only.
      </div>
    );

  const openNew = () => {
    setForm(emptyProduct);
    setEditing("new");
    setError("");
  };
  const openEdit = (p) => {
    setForm({
      ...p,
      notes_top: (p.notes_top || []).join(", "),
      notes_heart: (p.notes_heart || []).join(", "),
      notes_base: (p.notes_base || []).join(", "),
      gallery: (p.gallery || []).join(", "),
    });
    setEditing(p.id);
    setError("");
  };
  const closeForm = () => {
    setEditing(null);
    setError("");
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const payload = {
      ...form,
      price: parseFloat(form.price),
      size_ml: parseInt(form.size_ml, 10) || 100,
      stock: parseInt(form.stock, 10) || 0,
      featured: !!form.featured,
      notes_top: form.notes_top.split(",").map((s) => s.trim()).filter(Boolean),
      notes_heart: form.notes_heart.split(",").map((s) => s.trim()).filter(Boolean),
      notes_base: form.notes_base.split(",").map((s) => s.trim()).filter(Boolean),
      gallery: (form.gallery || "").split(",").map((s) => s.trim()).filter(Boolean),
    };
    try {
      if (editing === "new") {
        await api.post("/products", payload);
      } else {
        await api.put(`/products/${editing}`, payload);
      }
      await loadProducts();
      closeForm();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try {
      await api.delete(`/products/${id}`);
      await loadProducts();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const setStatus = async (id, status) => {
    try {
      await api.put(`/admin/orders/${id}/status`, null, { params: { status } });
      await loadOrders();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const change = (k) => (e) =>
    setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const input = "w-full bg-transparent border border-border px-3 py-2 focus:outline-none focus:border-primary text-sm";

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24" data-testid="admin-page">
      <div className="text-xs uppercase tracking-[0.32em] text-primary">Admin</div>
      <h1 className="font-serif text-5xl mt-4 tracking-tight">Command Post.</h1>

      <div className="mt-10 flex gap-6 border-b border-border">
        {[
          { key: "products", label: "Products" },
          { key: "orders", label: "Orders" },
          { key: "subscribers", label: "Subscribers" },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`admin-tab-${t.key}`}
            className={`pb-3 text-xs uppercase tracking-[0.28em] border-b-2 -mb-px transition-colors ${
              tab === t.key ? "text-primary border-primary" : "text-muted-foreground border-transparent hover:text-foreground"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "products" && (
        <div className="mt-8">
          <div className="flex justify-between items-center mb-6">
            <div className="text-sm text-muted-foreground">{products.length} products</div>
            <button
              onClick={openNew}
              data-testid="admin-new-product-btn"
              className="bg-primary text-primary-foreground uppercase tracking-[0.22em] text-xs px-5 py-3 flex items-center gap-2 hover:opacity-90"
            >
              <Plus size={14} /> New Product
            </button>
          </div>

          <div className="overflow-x-auto border border-border">
            <table className="w-full text-sm" data-testid="admin-products-table">
              <thead className="bg-secondary/40">
                <tr className="text-left text-xs uppercase tracking-[0.22em] text-muted-foreground">
                  <th className="p-4">Product</th>
                  <th className="p-4">Category</th>
                  <th className="p-4">Price</th>
                  <th className="p-4">Stock</th>
                  <th className="p-4">Featured</th>
                  <th className="p-4"></th>
                </tr>
              </thead>
              <tbody>
                {products.map((p) => (
                  <tr key={p.id} className="border-t border-border" data-testid={`admin-product-row-${p.id}`}>
                    <td className="p-4 flex items-center gap-3">
                      <div className="w-10 h-12 bg-secondary overflow-hidden flex-none">
                        <img src={p.image_url} alt="" className="w-full h-full object-cover" />
                      </div>
                      <div className="font-serif text-lg">{p.name}</div>
                    </td>
                    <td className="p-4 uppercase text-xs tracking-widest">{p.category}</td>
                    <td className="p-4">{formatGBP(p.price)}</td>
                    <td className="p-4">{p.stock}</td>
                    <td className="p-4">{p.featured ? "Yes" : "—"}</td>
                    <td className="p-4 text-right whitespace-nowrap">
                      <button
                        onClick={() => openEdit(p)}
                        data-testid={`admin-edit-${p.id}`}
                        className="text-muted-foreground hover:text-primary mr-4"
                      >
                        <Pencil size={16} />
                      </button>
                      <button
                        onClick={() => del(p.id)}
                        data-testid={`admin-delete-${p.id}`}
                        className="text-muted-foreground hover:text-destructive"
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "orders" && (
        <div className="mt-8">
          {orders.length === 0 ? (
            <div className="text-muted-foreground">No orders yet.</div>
          ) : (
            <ul className="space-y-4">
              {orders.map((o) => (
                <li key={o.id} className="border border-border p-5" data-testid={`admin-order-${o.id}`}>
                  <div className="flex flex-wrap justify-between gap-4">
                    <div>
                      <div className="text-xs uppercase tracking-[0.22em] text-muted-foreground">Order #{o.id.slice(0, 8)}</div>
                      <div className="text-sm mt-1">{new Date(o.created_at).toLocaleString()}</div>
                      <div className="text-sm mt-1 text-muted-foreground">
                        {o.address.full_name} · {o.address.city}, {o.address.state}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-serif text-2xl">{formatGBP(o.total)}</div>
                      <select
                        value={o.status}
                        onChange={(e) => setStatus(o.id, e.target.value)}
                        data-testid={`admin-order-status-${o.id}`}
                        className="mt-2 bg-transparent border border-border text-xs uppercase tracking-widest px-3 py-2 focus:outline-none focus:border-primary"
                      >
                        {["confirmed", "shipped", "delivered", "cancelled"].map((s) => (
                          <option key={s} value={s} className="bg-background">{s}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="mt-4 flex flex-wrap gap-4">
                    {o.items.map((it) => (
                      <div key={it.product_id} className="flex items-center gap-3">
                        <div className="w-12 h-14 bg-secondary overflow-hidden">
                          <img src={it.image_url} alt={it.name} className="w-full h-full object-cover" />
                        </div>
                        <div className="text-sm">
                          <div>{it.name}</div>
                          <div className="text-xs text-muted-foreground">Qty {it.quantity}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {tab === "subscribers" && (
        <div className="mt-8" data-testid="admin-subscribers-panel">
          <div className="flex flex-wrap justify-between items-end gap-4 mb-6">
            <div>
              <div className="text-sm text-muted-foreground">
                {subscribers.length} {subscribers.length === 1 ? "subscriber" : "subscribers"} · captured from the coming-soon page
              </div>
            </div>
            <button
              onClick={() => {
                if (subscribers.length === 0) return;
                const header = "email,source,created_at";
                const rows = subscribers.map((s) => [s.email, s.source || "", s.created_at || ""].join(","));
                const csv = [header, ...rows].join("\n");
                const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `aexents-subscribers-${new Date().toISOString().slice(0, 10)}.csv`;
                a.click();
                URL.revokeObjectURL(url);
              }}
              disabled={subscribers.length === 0}
              data-testid="admin-subscribers-export"
              className="border border-border hover:border-primary hover:text-primary text-xs uppercase tracking-[0.22em] px-5 py-3 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Export CSV
            </button>
          </div>

          {subscribers.length === 0 ? (
            <div className="border border-border p-10 text-center text-muted-foreground" data-testid="admin-subscribers-empty">
              No subscribers yet.
            </div>
          ) : (
            <div className="overflow-x-auto border border-border">
              <table className="w-full text-sm" data-testid="admin-subscribers-table">
                <thead className="bg-secondary/40">
                  <tr className="text-left text-xs uppercase tracking-[0.22em] text-muted-foreground">
                    <th className="p-4">Email</th>
                    <th className="p-4">Source</th>
                    <th className="p-4">Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {subscribers.map((s) => (
                    <tr key={s.id || s.email} className="border-t border-border" data-testid={`admin-subscriber-row-${s.email}`}>
                      <td className="p-4">{s.email}</td>
                      <td className="p-4 uppercase text-xs tracking-widest text-muted-foreground">
                        {s.source || "—"}
                      </td>
                      <td className="p-4 text-muted-foreground">
                        {s.created_at ? new Date(s.created_at).toLocaleString() : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="admin-product-modal">
          <div className="bg-background border border-border w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center px-6 py-4 border-b border-border">
              <div className="font-serif text-2xl">{editing === "new" ? "New Product" : "Edit Product"}</div>
              <button onClick={closeForm} className="text-muted-foreground hover:text-primary" data-testid="admin-modal-close">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={submit} className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <input placeholder="Name" required value={form.name} onChange={change("name")} data-testid="admin-form-name" className={input} />
              <input placeholder="Brand" value={form.brand} onChange={change("brand")} data-testid="admin-form-brand" className={input} />
              <textarea placeholder="Description" required rows={3} value={form.description} onChange={change("description")} data-testid="admin-form-description" className={`${input} md:col-span-2`} />
              <input placeholder="Top notes (comma-separated)" value={form.notes_top} onChange={change("notes_top")} data-testid="admin-form-notes-top" className={input} />
              <input placeholder="Heart notes (comma-separated)" value={form.notes_heart} onChange={change("notes_heart")} data-testid="admin-form-notes-heart" className={input} />
              <input placeholder="Base notes (comma-separated)" value={form.notes_base} onChange={change("notes_base")} data-testid="admin-form-notes-base" className={input} />
              <input type="number" step="0.01" placeholder="Price (GBP)" required value={form.price} onChange={change("price")} data-testid="admin-form-price" className={input} />
              <input type="number" placeholder="Size (ml)" value={form.size_ml} onChange={change("size_ml")} data-testid="admin-form-size" className={input} />
              <select value={form.category} onChange={change("category")} data-testid="admin-form-category" className={input}>
                {["her", "him", "unisex"].map((c) => (
                  <option key={c} value={c} className="bg-background">{c}</option>
                ))}
              </select>
              <select value={form.concentration} onChange={change("concentration")} data-testid="admin-form-concentration" className={input}>
                {["Eau de Toilette", "Eau de Parfum", "Extrait de Parfum"].map((c) => (
                  <option key={c} value={c} className="bg-background">{c}</option>
                ))}
              </select>
              <input placeholder="Image URL" required value={form.image_url} onChange={change("image_url")} data-testid="admin-form-image" className={`${input} md:col-span-2`} />
              <input placeholder="Gallery URLs (comma-separated)" value={form.gallery} onChange={change("gallery")} data-testid="admin-form-gallery" className={`${input} md:col-span-2`} />
              <input type="number" placeholder="Stock" value={form.stock} onChange={change("stock")} data-testid="admin-form-stock" className={input} />
              <label className="flex items-center gap-3 text-sm">
                <input type="checkbox" checked={form.featured} onChange={change("featured")} data-testid="admin-form-featured" className="accent-primary" />
                Featured on homepage
              </label>

              {error && <div className="text-destructive text-sm md:col-span-2" data-testid="admin-form-error">{error}</div>}

              <div className="md:col-span-2 flex justify-end gap-4 mt-4">
                <button type="button" onClick={closeForm} className="border border-border hover:border-primary px-6 py-3 uppercase tracking-[0.22em] text-xs">Cancel</button>
                <button type="submit" data-testid="admin-form-submit" className="bg-primary text-primary-foreground uppercase tracking-[0.22em] text-xs px-8 py-3 hover:opacity-90">
                  {editing === "new" ? "Create" : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
